import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  TextField,
  Button,
  Divider,
  Container,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Person as PersonIcon,
  Verified as VerifiedIcon,
  SaveOutlined as SaveOutlinedIcon,
  AccountCircleOutlined as AccountCircleOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import Swal from "sweetalert2";
import config from "../../Config";
import { PropagateLoader } from "react-spinners";
import ProfileSideBar from "./ProfileSideBar";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};

const Field = ({
  label,
  value,
  onChange,
  disabled = false,
  verified = false,
}) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.8,
        mb: 0.8,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: COLORS.grey,
        }}
      >
        {label}
      </Typography>

      {verified && (
        <Tooltip title={`${label} verified`}>
          <VerifiedIcon
            sx={{
              fontSize: 15,
              color: "#3F7D4C",
            }}
          />
        </Tooltip>
      )}
    </Box>

    <TextField
      value={value ?? ""}
      onChange={onChange}
      disabled={disabled}
      fullWidth
      size="small"
      variant="outlined"
      sx={{
        "& .MuiOutlinedInput-root": {
          minHeight: 48,
          borderRadius: 0,
          backgroundColor: disabled ? "#F1EEE8" : COLORS.white,
          fontSize: 14,
          color: COLORS.black,
          "& fieldset": {
            borderColor: COLORS.border,
          },
          "&:hover fieldset": {
            borderColor: COLORS.lightGrey,
          },
          "&.Mui-focused fieldset": {
            borderColor: COLORS.black,
            borderWidth: 1,
          },
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: COLORS.grey,
        },
      }}
    />
  </Box>
);

export default function ProfilePage() {
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const userId = sessionStorage.getItem("userId");

  const fetchUserData = useCallback(async () => {
    if (!userId) {
      navigate("/Signin");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        `${config.apiBaseUrl}/${config.userProfile}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "user-id": userId,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user");
      }

      const data = await response.json();

      if (!data?.User) {
        throw new Error("User data not found");
      }

      setProfileData(data.User);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load profile",
        text: "We couldn't load your profile details. Please try again.",
        confirmButtonColor: COLORS.black,
        background: COLORS.white,
        color: COLORS.black,
      });
    } finally {
      setLoading(false);
    }
  }, [navigate, userId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleSaveChanges = async () => {
    if (!profileData || !userId) {
      return;
    }

    setIsSaving(true);

    const updatedData = {
      name: profileData.name || "",
      contact: profileData.contact || "",
      email: profileData.email || "",
      user_DOB: profileData.user_DOB || "",
      gender: profileData.gender || "",
    };

    try {
      const response = await fetch(
        `${config.apiBaseUrl}/${config.updateUserProfile}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "user-id": userId,
          },
          body: JSON.stringify(updatedData),
        },
      );

      if (!response.ok) {
        throw new Error("Profile update failed");
      }

      await Swal.fire({
        icon: "success",
        title: "Profile Updated",
        text: "Your profile has been updated successfully.",
        confirmButtonColor: COLORS.black,
        background: COLORS.white,
        color: COLORS.black,
      });

      await fetchUserData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "There was an error updating your profile. Please try again.",
        confirmButtonColor: COLORS.black,
        background: COLORS.white,
        color: COLORS.black,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "70vh",
            backgroundColor: COLORS.cream,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PropagateLoader color={COLORS.gold} size={9} />

          <Typography
            sx={{
              mt: 2,
              fontFamily: "Georgia, serif",
              fontSize: 20,
              color: COLORS.black,
            }}
          >
            Loading your profile
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: COLORS.grey,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Please wait
          </Typography>
        </Box>

        <Footer />
      </>
    );
  }

  if (!profileData) {
    return (
      <>
        <Navbar />

        <Box
          sx={{
            minHeight: "70vh",
            backgroundColor: COLORS.cream,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 500,
              p: 5,
              borderRadius: 0,
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.white,
              textAlign: "center",
            }}
          >
            <AccountCircleOutlinedIcon
              sx={{
                fontSize: 46,
                color: COLORS.gold,
                mb: 2,
              }}
            />

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: 28,
                mb: 1,
              }}
            >
              Profile unavailable
            </Typography>

            <Typography
              sx={{
                fontSize: 13,
                lineHeight: 1.7,
                color: COLORS.grey,
                mb: 3,
              }}
            >
              Please sign in again to access your account.
            </Typography>

            <Button
              onClick={() => navigate("/Signin")}
              sx={{
                borderRadius: 0,
                backgroundColor: COLORS.black,
                color: COLORS.white,
                px: 4,
                py: 1.4,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                "&:hover": {
                  backgroundColor: COLORS.mutedBlack,
                },
              }}
            >
              Sign In
            </Button>
          </Paper>
        </Box>

        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: COLORS.cream,
          color: COLORS.black,
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: 1440,
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 4, md: 7 },
          }}
        >
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: COLORS.gold,
                mb: 1.2,
              }}
            >
              Your Account
            </Typography>

            <Typography
              sx={{
                fontFamily: "Georgia, serif",
                fontSize: { xs: 34, sm: 42, md: 52 },
                lineHeight: 1.05,
                fontWeight: 400,
                color: COLORS.black,
                mb: 1.5,
              }}
            >
              Your profile,
              <br />
              your wardrobe.
            </Typography>

            <Typography
              sx={{
                maxWidth: 600,
                fontSize: 14,
                lineHeight: 1.8,
                color: COLORS.grey,
              }}
            >
              Keep your personal details up to date and make your Hindustan
              Wardrobe experience truly yours.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: { xs: 0, md: 5 },
              alignItems: "start",
            }}
          >
            <ProfileSideBar active="profile" />

            <Box sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.white,
                  borderRadius: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2.5, sm: 4 },
                    py: 3,
                    backgroundColor: COLORS.softCream,
                    borderBottom: `1px solid ${COLORS.border}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px solid ${COLORS.border}`,
                        backgroundColor: COLORS.white,
                        flexShrink: 0,
                      }}
                    >
                      <PersonIcon
                        sx={{
                          color: COLORS.gold,
                          fontSize: 21,
                        }}
                      />
                    </Box>

                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: 24,
                          color: COLORS.black,
                        }}
                      >
                        Edit Profile
                      </Typography>

                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: 12,
                          color: COLORS.grey,
                        }}
                      >
                        Update your personal information.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 4,
                      pb: 3,
                      borderBottom: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <Avatar
                      src={profileData.avatar || "/placeholder.svg"}
                      alt={profileData.name || "Profile"}
                      sx={{
                        width: 76,
                        height: 76,
                        border: `2px solid ${COLORS.border}`,
                        backgroundColor: COLORS.cream,
                      }}
                    />

                    <Box sx={{ textAlign: "left", minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontFamily: "Georgia, serif",
                          fontSize: { xs: 21, sm: 24 },
                          color: COLORS.black,
                          wordBreak: "break-word",
                        }}
                      >
                        {profileData.name || "Your Name"}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        sx={{ mt: 0.6 }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: COLORS.grey,
                          }}
                        >
                          Hindustan Wardrobe member
                        </Typography>

                        {(profileData.isMobileVerified ||
                          profileData.isEmailVerified) && (
                          <VerifiedIcon
                            sx={{
                              fontSize: 15,
                              color: "#3F7D4C",
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2.5,
                    }}
                  >
                    <Field
                      label="Name"
                      value={profileData.name}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          name: e.target.value,
                        })
                      }
                    />

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        gap: 2,
                      }}
                    >
                      <Field
                        label="Date of Birth"
                        value={profileData.user_DOB}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            user_DOB: e.target.value,
                          })
                        }
                      />

                      <Field
                        label="Gender"
                        value={profileData.gender}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            gender: e.target.value,
                          })
                        }
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                        },
                        gap: 2,
                      }}
                    >
                      <Field
                        label="Contact Number"
                        value={profileData.contact}
                        disabled={profileData.isMobileVerified}
                        verified={profileData.isMobileVerified}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            contact: e.target.value,
                          })
                        }
                      />

                      <Field
                        label="Email"
                        value={profileData.email}
                        disabled={profileData.isEmailVerified}
                        verified={profileData.isEmailVerified}
                        onChange={(e) =>
                          setProfileData({
                            ...profileData,
                            email: e.target.value,
                          })
                        }
                      />
                    </Box>
                  </Box>

                  <Divider
                    sx={{
                      my: 4,
                      borderColor: COLORS.border,
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      startIcon={
                        !isSaving ? (
                          <SaveOutlinedIcon sx={{ fontSize: 18 }} />
                        ) : null
                      }
                      sx={{
                        minWidth: 170,
                        minHeight: 48,
                        borderRadius: 0,
                        backgroundColor: COLORS.black,
                        color: COLORS.white,
                        px: 3,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        "&:hover": {
                          backgroundColor: COLORS.mutedBlack,
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#B9B4AC",
                          color: COLORS.white,
                        },
                      }}
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                  </Box>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.black,
                  color: COLORS.white,
                  borderRadius: 0,
                  p: { xs: 2.5, sm: 3.5 },
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: COLORS.goldLight,
                        mb: 0.8,
                      }}
                    >
                      Profile Security
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 23,
                        mb: 0.6,
                      }}
                    >
                      Keep your details current.
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#BDB8B0",
                        lineHeight: 1.7,
                      }}
                    >
                      Verified contact details help keep your account secure.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      border: `1px solid ${COLORS.goldLight}`,
                      px: 2,
                      py: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: COLORS.goldLight,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    >
                      Account Active
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
